import DailyIframe, { type DailyCall } from '@daily-co/daily-js';
import { useEffect, useRef, useState } from 'react';
import './CallFrame.css';

/**
 * Daily Prebuilt, embedded (§2 — Prebuilt for v1 speed; a custom UI comes later).
 *
 * Prebuilt handles video, mic, camera and leave. The cart gets ONE overlay of our
 * own — the screenshot button in Phase 6 — and guests get plain Prebuilt (§6).
 *
 * The iframe loads from *.daily.co, which is why the CSP allows `frame-src` there
 * and nothing else. The bundle itself is served from our own origin, so
 * `script-src 'self'` still holds.
 */

export function CallFrame({
  roomUrl,
  token,
  onLeft,
  onJoined,
  overlay,
}: {
  roomUrl: string;
  token: string;
  onLeft?: () => void;
  onJoined?: () => void;
  /** Cart-only controls drawn over the video well. */
  overlay?: React.ReactNode;
}) {
  const holder = useRef<HTMLDivElement | null>(null);
  const frame = useRef<DailyCall | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!holder.current || frame.current) return;

    let call: DailyCall;
    try {
      call = DailyIframe.createFrame(holder.current, {
        showLeaveButton: true,
        showFullscreenButton: false,
        iframeStyle: { width: '100%', height: '100%', border: '0' },
      });
    } catch (err) {
      console.error('[call] could not create the video frame', err);
      setError('The video could not start on this device.');
      return;
    }

    frame.current = call;

    // Presence comes live from Daily's own events, not from polling (§0.5).
    call.on('left-meeting', () => onLeft?.());
    call.on('joined-meeting', () => onJoined?.());
    call.on('error', (e) => {
      console.error('[call] daily error', e);
      setError('The video connection had a problem. Please try again.');
    });

    void call.join({ url: roomUrl, token }).catch((err) => {
      console.error('[call] join failed', err);
      setError('Could not connect to the call. Please check the connection and try again.');
    });

    return () => {
      frame.current = null;
      void call.destroy().catch(() => {
        /* the frame is going away regardless */
      });
    };
    // Room and token are fixed for the life of this component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomUrl, token]);

  return (
    <div className="callframe">
      <div className="callframe__well" ref={holder} />
      {overlay ? <div className="callframe__overlay">{overlay}</div> : null}
      {error ? (
        <div className="callframe__error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}
